import React, { useMemo } from 'react'
import { mediaQueries, palette } from '@centivo/react-components'
import { useGlobalState } from 'src/context/global'
import styled, { css } from 'styled-components'

export interface FooterProps {
  isBannerOpen?: boolean
  children?: React.ReactNode
}

const EligibilityFooter = styled.footer<FooterProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 90%;
  max-width: 1106px;
  margin: 84px auto 0;
  border-top: 1px solid ${({ theme }) => theme.palette.brand.lightPurple};
  padding-top: 20px;

  > div.content {
    color: #212121;
    font-size: 1rem;
    letter-spacing: -0.32px;
    font-weight: 400;
    margin-bottom: 1.25rem;
  }

  > nav ul {
    display: flex;

    a,
    p {
      font-size: 1rem;
      color: #212121;
      letter-spacing: -0.32px;
      font-weight: 400;
    }

    a:hover,
    a:focus {
      text-decoration: underline;
    }

    li:not(:last-child) {
      padding-right: 8px;
      margin-right: 8px;
      border-right: 1px solid #cac1e6;
    }
  }

  @media ${mediaQueries.mobile} {
    margin-top: 40px;
    padding-bottom: 54px;
    flex-direction: column;

    > nav ul {
      flex-wrap: wrap;
    }
  }
`

const FooterContainer = styled.footer<FooterProps>`
  display: flex;
  flex-direction: row;
  font-size: 11px;
  font-weight: 500;
  min-height: 5rem;
  padding-bottom: 1.25rem;
  padding-top: 1rem;
  width: 100vw;
  align-items: center;
  transition: margin-bottom 1s ease;

  ${({ theme, isBannerOpen }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          ${isBannerOpen ? 'margin-bottom: 2.75rem;' : ''}

          background-color: #F7F4E6;
          color: ${palette.brand.darkGreen};

          > nav ul {
            color: ${palette.brand.darkGreen};

            a {
              font-weight: 400;
            }
          }
        `
      default:
        return css`
          ${isBannerOpen ? 'margin-bottom: 1.5rem;' : ''}
          background-color: ${palette.brand.darkGreen};
          color: ${palette.brand.lightGreen};

          > nav ul {
            color: ${palette.brand.lightGreen};

            a {
              font-weight: 600;
            }
          }
        `
    }
  }}

  > nav {
    margin-left: auto;
    margin-right: 6rem;
  }

  > nav ul {
    display: flex;
    a {
      color: inherit;
      font-size: 1rem;
      &:hover,
      &:focus {
        text-decoration: underline;
      }
    }

    li:not(:first-child) {
      margin-left: 2rem;
    }
  }

  > .content {
    align-self: center;
    flex-grow: 1;
    padding: 0 1.25rem;

    ${({ theme }) => theme.screenWidth}

    @media ${mediaQueries.desktop} {
      padding: 0 2rem;
      justify-content: right;
    }

    a {
      color: inherit;
      &:hover {
        color: inherit;
      }
    }
  }
`

export const Footer: React.FC<FooterProps> = ({ isBannerOpen, children }) => {
  const [{ appConfig }] = useGlobalState()

  const Container = useMemo(
    () =>
      appConfig.template === 'template-eligibility'
        ? EligibilityFooter
        : FooterContainer,
    [appConfig.template]
  )

  return (
    <Container isBannerOpen={isBannerOpen}>
      {children}
      <nav>
        <ul>
          {appConfig.template === 'template-eligibility' && (
            <li>
              <p>All rights reserved</p>
            </li>
          )}
          <li>
            <a
              href="https://centivo-provider-employer-resources.s3.amazonaws.com/603_Centivo_Terms_Conditions.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Terms & Conditions
            </a>
          </li>
          <li>
            <a
              href="https://centivo.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
          </li>
          <li>
            <a
              href="https://centivo.com/accessibility/"
              target="_blank"
              rel="noreferrer"
            >
              Accessibility Statement
            </a>
          </li>
        </ul>
      </nav>
    </Container>
  )
}
